import { useCallback, useMemo } from 'react';
import { useAppSelector } from 'src/utils/Hooks';
import useDorisMarketShareHooks from './useDorisMarketShareHooks';
import { useDorisApplyEntityFilters } from 'src/utils/Hooks/useDorisApplyEntityFilters';
import { useDorisData } from 'src/utils/Hooks/useDorisData';

export interface DorisRecord {
  fieldKey: string;
  value: number;
  marketShareValue?: number;
  metadata?: {
    searchTerm: string;
    [key: string]: any;
  };
}

export interface DorisResponse {
  records: DorisRecord[];
  fieldName: string;
  debug?: {
    query: string;
    queryExecutionTime: number;
    queryContextCreationTime: number;
    isCacheHit: boolean;
    cacheResponseTime: number;
    topicGroupAnnotationTime: number;
  };
}

export interface UseAtlasV2DorisColumnDataProps {
  metricType: string;
  fieldName: string;
  queryKey: string;
  endPoint: string;
  showMarketShare?: boolean;
  shouldFetchComparison?: boolean;
  additionalParams?: Record<string, any>;
  resultCount?: number;
  // Optional custom filters - these will be enhanced by useDorisApplyEntityFilters
  // but can be used to override specific filters if needed
  brandIds?: number[];
  categoryIds?: number[];
  subcategoryIds?: number[];
  segmentIds?: string[];
  minRetailPrice?: number;
  maxRetailPrice?: number;
  keywords?: string[];
  enableFetch?: boolean;
  /**
   * Optional custom function to calculate market share values
   * If provided, this will be used instead of the default market share calculation
   */
  marketShareCalculator?: (value: number, isComparison?: boolean) => number;
}

/**
 * A hook to fetch Doris column chart data, with optional market share calculation
 * Uses useDorisApplyEntityFilters to respect entity and app filters automatically
 */
export default function useAtlasV2DorisColumnData({
  metricType,
  fieldName,
  queryKey,
  endPoint,
  showMarketShare = false,
  shouldFetchComparison = true,
  additionalParams = {},
  resultCount = 100,
  brandIds,
  categoryIds,
  subcategoryIds,
  segmentIds,
  minRetailPrice,
  maxRetailPrice,
  keywords,
  enableFetch = true,
  marketShareCalculator
}: UseAtlasV2DorisColumnDataProps) {
  const { marketShareRequestBuilder } = useDorisMarketShareHooks();
  const applyEntityFilters = useDorisApplyEntityFilters();

  const { startWeek: mainStartWeekId, endWeek: mainEndWeekId } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeekId, endWeek: comparisonEndWeekId } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const retailerId = useAppSelector((state) => state.retailer?.id);

  // Add selectors for categories and subcategories
  const categories = useAppSelector((state) => state.categories);
  const subCategories = useAppSelector((state) => state.subCategories);

  // Apply entity filters to get the filtered IDs - follow the pattern from useDorisMetricByWeekId
  // This takes any custom filters provided but enhances them with entity context
  const baseParams = useMemo(() => {
    const initialParams = {
      fieldName,
      metricType,
      retailerId: +retailerId,
      categoryIds,
      subcategoryIds,
      brandIds,
      segmentIds,
      minRetailPrice,
      maxRetailPrice,
      keywords,
      resultCount,
      businessunitids: []
    };

    // Apply entity and active filters
    return applyEntityFilters(initialParams);
  }, [
    fieldName,
    metricType,
    retailerId,
    categoryIds,
    subcategoryIds,
    brandIds,
    segmentIds,
    minRetailPrice,
    maxRetailPrice,
    keywords,
    resultCount,
    applyEntityFilters
  ]);

  // Fetch primary data using useDorisData

  const primaryRequest = useMemo(() => {
    return {
      methodName: endPoint,
      metricType,
      fieldName,
      queryKey: `${queryKey}_${mainStartWeekId}_${mainEndWeekId}_${fieldName}`,
      resultCount,
      categoryIds: baseParams.categoryIds,
      subcategoryIds: baseParams.subcategoryIds,
      brandIds: baseParams.brandIds,
      segmentIds: baseParams.segmentIds,
      minRetailPrice: baseParams.minRetailPrice,
      maxRetailPrice: baseParams.maxRetailPrice,
      keywords,
      enabled: enableFetch,
      customTimePeriods: {
        startWeekId: mainStartWeekId,
        endWeekId: mainEndWeekId
      },
      skipComparison: true,
      additionalParams: additionalParams || {},
      queryOptions: {
        refetchOnWindowFocus: false,
        staleTime: 60 * 10 * 1000,
        retry: 1
      }
    };
  }, [
    endPoint,
    metricType,
    fieldName,
    queryKey,
    mainStartWeekId,
    mainEndWeekId,
    resultCount,
    baseParams.categoryIds,
    baseParams.subcategoryIds,
    baseParams.brandIds,
    baseParams.segmentIds,
    baseParams.minRetailPrice,
    baseParams.maxRetailPrice,
    keywords,
    enableFetch,
    additionalParams
  ]);

  const {
    data: primaryData,
    isLoading: isPrimaryLoading,
    error: primaryError
  } = useDorisData<DorisResponse>(primaryRequest);

  // Extract the field keys (keywords) from primary data for subsequent requests
  const fieldKeys = useMemo(() => {
    return primaryData?.records?.map((record) => record.fieldKey) || [];
  }, [primaryData]);

  // Determine if we should include keywords filter in subsequent requests
  const shouldIncludeKeywords = useMemo(() => {
    // Only include keywords filter if the fieldName is related to keywords
    return fieldName === 'keyword' || fieldName === 'searchTerm';
  }, [fieldName]);

  // Market share primary request (main time period without brand filter)
  const marketSharePrimaryRequestData = useMemo(() => {
    if (!showMarketShare || !fieldKeys.length || marketShareCalculator) {
      return null;
    }

    const requestParams = {
      ...baseParams,
      startWeekId: mainStartWeekId,
      endWeekId: mainEndWeekId,
      ...additionalParams
    };

    // Only add keywords filter if we should include it
    if (shouldIncludeKeywords) {
      (requestParams as any).keywords = fieldKeys;
    }

    // Apply market share request modifications
    return marketShareRequestBuilder(requestParams);
  }, [
    showMarketShare,
    fieldKeys,
    marketShareCalculator,
    baseParams,
    mainStartWeekId,
    mainEndWeekId,
    additionalParams,
    shouldIncludeKeywords,
    marketShareRequestBuilder
  ]);

  // Fetch market share primary data using useDorisData
  const { data: marketSharePrimaryData, isLoading: isMarketSharePrimaryLoading } = useDorisData<DorisResponse>({
    methodName: endPoint,
    metricType,
    fieldName,
    queryKey: `market_${queryKey}_${mainStartWeekId}_${mainEndWeekId}_${fieldName}`,
    resultCount,
    customTimePeriods: {
      startWeekId: mainStartWeekId,
      endWeekId: mainEndWeekId
    },
    skipComparison: true,
    additionalParams: marketSharePrimaryRequestData || {},
    enabled: showMarketShare && !!marketSharePrimaryRequestData && fieldKeys.length > 0,
    queryOptions: {
      refetchOnWindowFocus: false,
      staleTime: 60 * 10 * 1000,
      retry: 1
    }
  });

  // Comparison request (comparison time period with brand filter)
  const comparisonRequestData = useMemo(() => {
    if (!shouldFetchComparison || !fieldKeys.length) {
      return null;
    }

    const requestParams = {
      ...baseParams,
      startWeekId: comparisonStartWeekId,
      endWeekId: comparisonEndWeekId,
      ...additionalParams
    };

    // Only add keywords filter if we should include it
    if (shouldIncludeKeywords) {
      (requestParams as any).keywords = fieldKeys;
    }

    // Apply entity-specific request modifications
    return requestParams;
  }, [
    shouldFetchComparison,
    fieldKeys,
    baseParams,
    comparisonStartWeekId,
    comparisonEndWeekId,
    additionalParams,
    shouldIncludeKeywords
  ]);

  const comparisonRequest = useMemo(() => {
    return {
      methodName: endPoint,
      metricType,
      fieldName,
      resultCount,
      customTimePeriods: {
        startWeekId: comparisonStartWeekId,
        endWeekId: comparisonEndWeekId
      },
      skipComparison: true,
      additionalParams: comparisonRequestData || {},
      enabled: shouldFetchComparison && !!comparisonRequestData && fieldKeys.length > 0,
      queryOptions: {
        refetchOnWindowFocus: false,
        staleTime: 60 * 10 * 1000,
        retry: 1
      }
    };
  }, [
    endPoint,
    metricType,
    fieldName,
    resultCount,
    comparisonStartWeekId,
    comparisonEndWeekId,
    comparisonRequestData,
    shouldFetchComparison,
    fieldKeys.length
  ]);

  // Fetch comparison data using useDorisData
  const { data: comparisonData, isLoading: isComparisonLoading } = useDorisData<DorisResponse>({
    methodName: endPoint,
    metricType,
    fieldName,
    resultCount,
    customTimePeriods: {
      startWeekId: comparisonStartWeekId,
      endWeekId: comparisonEndWeekId
    },
    skipComparison: true,
    additionalParams: comparisonRequestData || {},
    enabled: shouldFetchComparison && !!comparisonRequestData && fieldKeys.length > 0,
    queryOptions: {
      refetchOnWindowFocus: false,
      staleTime: 60 * 10 * 1000,
      retry: 1
    }
  });

  // Market share comparison request (comparison time period without brand filter)
  const marketShareComparisonRequestData = useMemo(() => {
    if (!showMarketShare || !shouldFetchComparison || !fieldKeys.length || marketShareCalculator) {
      return null;
    }

    const requestParams = {
      ...baseParams,
      startWeekId: comparisonStartWeekId,
      endWeekId: comparisonEndWeekId,
      ...additionalParams
    };

    // Only add keywords filter if we should include it
    if (shouldIncludeKeywords) {
      (requestParams as any).keywords = fieldKeys;
    }

    // Apply market share request modifications
    return marketShareRequestBuilder(requestParams);
  }, [
    showMarketShare,
    shouldFetchComparison,
    fieldKeys,
    marketShareCalculator,
    baseParams,
    comparisonStartWeekId,
    comparisonEndWeekId,
    additionalParams,
    shouldIncludeKeywords,
    marketShareRequestBuilder
  ]);

  // Fetch market share comparison data using useDorisData
  const { data: marketShareComparisonData, isLoading: isMarketShareComparisonLoading } = useDorisData<DorisResponse>({
    methodName: endPoint,
    metricType,
    fieldName,
    queryKey: `market_comparison_${queryKey}_${comparisonStartWeekId}_${comparisonEndWeekId}_${fieldName}`,
    resultCount,
    customTimePeriods: {
      startWeekId: comparisonStartWeekId,
      endWeekId: comparisonEndWeekId
    },
    skipComparison: true,
    additionalParams: marketShareComparisonRequestData || {},
    enabled: showMarketShare && shouldFetchComparison && !!marketShareComparisonRequestData && fieldKeys.length > 0,
    queryOptions: {
      refetchOnWindowFocus: false,
      staleTime: 60 * 10 * 1000,
      retry: 1
    }
  });

  // Create a map for easier data access
  const createFieldKeyToValueMap = useCallback((data: DorisResponse | undefined) => {
    if (!data?.records) {
      return {};
    }
    return data.records.reduce((acc, record) => {
      acc[record.fieldKey] = typeof record.value === 'number' && !Number.isNaN(record.value) ? record.value : 0;
      return acc;
    }, {} as Record<string, number>);
  }, []);

  // Maps for all data types
  const primaryMap = useMemo(() => createFieldKeyToValueMap(primaryData), [createFieldKeyToValueMap, primaryData]);
  const marketSharePrimaryMap = useMemo(
    () => createFieldKeyToValueMap(marketSharePrimaryData),
    [createFieldKeyToValueMap, marketSharePrimaryData]
  );
  const comparisonMap = useMemo(
    () => createFieldKeyToValueMap(comparisonData),
    [createFieldKeyToValueMap, comparisonData]
  );
  const marketShareComparisonMap = useMemo(
    () => createFieldKeyToValueMap(marketShareComparisonData),
    [createFieldKeyToValueMap, marketShareComparisonData]
  );

  // Format the data to match the expected structure for AtlasV2ColumnChart
  const parsedData = useMemo(() => {
    if (isPrimaryLoading || !primaryData?.records) {
      return [];
    }

    // Create lookup maps for categories and subcategories
    const categoryMap = categories.reduce((acc, category) => {
      if (category.categoryId !== undefined) {
        acc[category.categoryId] = category;
      }
      return acc;
    }, {});

    const subCategoryMap = subCategories.reduce((acc, subCategory) => {
      if (subCategory.subCategoryId !== undefined) {
        acc[subCategory.subCategoryId] = subCategory;
      }
      return acc;
    }, {});

    const useDirectValues = primaryData.records.length > 0 && typeof primaryData.records[0].value === 'number';

    const result = primaryData.records.map((record) => {
      const { fieldKey } = record;

      const primaryValue = useDirectValues ? record.value : primaryMap[fieldKey] || 0;

      // Calculate market share for primary
      let finalPrimaryValue = primaryValue;

      if (showMarketShare) {
        if (marketShareCalculator) {
          // Use the custom calculator if provided - for primary data
          finalPrimaryValue = marketShareCalculator(primaryValue);
        } else if (marketSharePrimaryMap[fieldKey] && marketSharePrimaryMap[fieldKey] > 0) {
          // Use the default calculation
          finalPrimaryValue = primaryValue / marketSharePrimaryMap[fieldKey];
        }
      }

      // Handle comparison data
      const comparisonValue = comparisonMap[fieldKey] || 0;
      let finalComparisonValue = comparisonValue;

      // Calculate market share for comparison
      if (showMarketShare) {
        if (marketShareCalculator) {
          // Use the custom calculator with isComparison flag for comparison data
          finalComparisonValue = marketShareCalculator(comparisonValue, true);
        } else if (marketShareComparisonMap[fieldKey] && marketShareComparisonMap[fieldKey] > 0) {
          // Use the default calculation
          finalComparisonValue = comparisonValue / marketShareComparisonMap[fieldKey];
        }
      }

      // TODO :: // Backend replace this with useEntityMetadataEnhancer
      const enhancedMetadata = { ...(record.metadata || {}) };

      const fieldNameLower = primaryData.fieldName.toLowerCase();

      // TODO :: // Backend is returning categoryid and category, requested unified field name, once it is unified, clear the code
      if (fieldNameLower === 'categoryid' || fieldNameLower === 'category') {
        const categoryId = Number(fieldKey);

        if (categoryMap[categoryId]) {
          enhancedMetadata.categoryId = categoryId;
          enhancedMetadata.categoryName = categoryMap[categoryId].name;
        }
      }
      // Handle Subcategory data
      // TODO :: // Backend is returning subcategoryid and subcategory, requested unified field name, once it is unified, clear the code
      else if (fieldNameLower === 'subcategoryid' || fieldNameLower === 'subcategory') {
        const subCategoryId = Number(fieldKey);
        if (subCategoryMap[subCategoryId]) {
          enhancedMetadata.subCategoryId = subCategoryId;
          enhancedMetadata.subCategoryName = subCategoryMap[subCategoryId].name;
          enhancedMetadata.parentCategoryId = subCategoryMap[subCategoryId].parentCategoryId;
        }
      } else if (fieldNameLower === 'brand') {
        enhancedMetadata.brandId = Number(fieldKey);

        enhancedMetadata.brandName = enhancedMetadata.brandName || `${fieldKey}`;
      }

      // Ensure we have a name for display

      // TODO :: // Backend is returning not unified fieldName, once it is unified, clear the code
      let displayName: string;

      switch (fieldNameLower) {
        case 'categoryid':
        case 'category':
          displayName = categoryMap[Number(fieldKey)]?.name ?? `${fieldKey}`;
          break;
        case 'subcategoryid':
        case 'subcategory':
          displayName = subCategoryMap[Number(fieldKey)]?.name ?? `${fieldKey}`;
          break;
        default:
          displayName = enhancedMetadata.brandName ?? `${fieldKey}`;
          break;
      }

      return {
        fieldId: fieldKey,
        values: {
          [fieldName]: finalPrimaryValue
        },
        comparisonValues: {
          [fieldName]: finalComparisonValue
        },
        metadata: {
          name: displayName,
          ...enhancedMetadata
        }
      };
    });

    return result;
  }, [
    isPrimaryLoading,
    primaryData,
    primaryMap,
    comparisonMap,
    marketSharePrimaryMap,
    marketShareComparisonMap,
    showMarketShare,
    fieldName,
    marketShareCalculator,
    categories,
    subCategories
  ]);

  const isLoading =
    isPrimaryLoading ||
    (showMarketShare && isMarketSharePrimaryLoading) ||
    (shouldFetchComparison && isComparisonLoading) ||
    (showMarketShare && shouldFetchComparison && isMarketShareComparisonLoading);

  return {
    data: parsedData,
    isLoading,
    error: primaryError,
    primaryRequest,
    comparisonRequest
  };
}
